import { useSelector } from 'react-redux';
import { SearchX, Sparkles } from 'lucide-react';
import { RootState } from '../../../store/store';
import { useAssetCart } from '@/components/ui/carousel';
import { Typography } from '@/components/ui/typography';
import { EmptyState } from '@/components/ui/empty-state';
import AssetsImageCard from '../property-asset-view/asset-images/AssetsImageCard';
import { isInternalAssetTeamUser } from '../assetFeatureFlags';
import { canStagePhoto, isStagedAssetInView } from '../model/assetViewScope';

type StagedAssetsGridProps = {
  onImageClick: (index: number) => void;
  onDownload: (photoUUID: string) => void;
  /** Reopens the staging modal in restage mode (DV-2252); supplied only when
   * the page's virtual-staging gate passes. */
  onStage?: (photoUUID: string) => void;
  /**
   * The page's drop overlay is up over this grid. Its text is centered right
   * where an empty grid's placeholder sits, so the placeholder steps aside
   * (invisible, keeping its space) until the drag ends. Photos stay visible:
   * the overlay is translucent so they show through.
   */
  dropOverlayActive?: boolean;
};

/**
 * "Virtual Staging" tab content. Internal users see EVERY staged photo,
 * including ones hidden in the Photos tab (DV-2740) — hidden ones render
 * dimmed with a hover reveal.
 *
 * DV-2820: customers see only NON-hidden staged photos. Hiding a staged photo
 * is how internal staff park unapproved AI output, and once staging runs in
 * prod that work-in-progress sits on a real customer's property — the tab
 * became customer-visible in the same release, so showing it unfiltered would
 * present unapproved renders as if they were deliverable. The tab's own
 * visibility gate applies the same rule (see showStagedTab).
 *
 * Mostly view-only relative to the Photos tab: no upload tile, no
 * drag-reorder, no hide toggle, no delete. Card actions are download
 * (everyone) and regenerate (when the page supplies onStage, DV-2252).
 */
const StagedAssetsGrid = ({
  onImageClick,
  onDownload,
  onStage,
  dropOverlayActive = false
}: StagedAssetsGridProps) => {
  const imageAssets = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );
  const searchQuery = useSelector(
    (state: RootState) => state.imageAsset.searchQuery
  );
  const { cartAssetIdSet } = useAssetCart(true);
  const seesHiddenStaged = isInternalAssetTeamUser();
  // Keep each card's ORIGINAL index: the viewer receives a filtered list, so
  // the page remaps by photoUUID (same rule as AssetsImageGrid).
  const stagedEntries = imageAssets
    .map((image, index) => ({ image, index }))
    .filter(
      ({ image }) =>
        // DV-2856: search scope is the active tab, so the staged grid narrows
        // by the same query the tab's count and Select All do
        // (model/assetViewScope). The role rule is this grid's own: only
        // internal users see hidden staged WIP (DV-2740 / DV-2820).
        isStagedAssetInView(image, { searchQuery }) &&
        (seesHiddenStaged || !image.hidden)
    );

  if (stagedEntries.length === 0) {
    const placeholderClass = dropOverlayActive ? 'invisible' : undefined;
    // A filtered-to-nothing tab must not claim the property has no staged
    // photos — that's a different fact, and the Sparkles hero below states it.
    // Same no-match hero the Photos grid shows (decided 2026-09-18, Q3b).
    if (searchQuery.trim() !== '') {
      return (
        <EmptyState
          icon={SearchX}
          title="No Matches"
          description="Try adjusting your filters or search criteria"
          className={placeholderClass}
        />
      );
    }
    return (
      <EmptyState
        icon={Sparkles}
        title="No Virtually Staged Photos Yet"
        description="Stage photos with AI from the Photos tab, or upload staged images here."
        className={placeholderClass}
      />
    );
  }

  return (
    <>
      <Typography as="p" variant="body" className="mb-4 text-muted-foreground">
        {seesHiddenStaged
          ? 'Virtually staged photos for this property, including ones hidden in the Photos tab (shown dimmed).'
          : 'Virtually staged photos for this property.'}
      </Typography>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(266px,1fr))] gap-3">
        {stagedEntries.map(({ image, index }) => (
          <AssetsImageCard
            key={image.photoUUID}
            photoUUID={image.photoUUID}
            index={index}
            inCart={cartAssetIdSet.has(image.assetUUID)}
            onImageClick={onImageClick}
            onDownload={onDownload}
            onStage={canStagePhoto(image) ? onStage : undefined}
            variant="staged"
          />
        ))}
      </div>
    </>
  );
};

export default StagedAssetsGrid;
